import { uniqueId } from "lodash";
import { useContext, useEffect } from "react";
import { useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { BiMinus, BiPlus, BiCheck } from "react-icons/bi";
import { MdOutlineQrCode2 } from "react-icons/md";
import { Translate, withLocalize } from "react-localize-redux";
import API from "../../../utils/API";
import AlertModal from "../../../utils/components/AlertModal";
import Checkbox from "../../../utils/components/Checkbox";
import Input from "../../../utils/components/Input";
import Loading from "../../../utils/components/Loading";
import Modal from "../../../utils/components/Modal";
import { useProject } from "../../../utils/hooks/useProject";
import { SET_TYPES } from "./Sets";
import SubTreeItem from "./SubTreeItem";
import InformModal from "../../../utils/components/InformModal";
import { RequestContext } from "../../../utils/context/RequestContext";

export const LEVEL_COLORS = [
  "#eaf5ff",
  "#d6e4f0",
  "#c8dae8",
  "#b8d0e2",
  "#a8c6dc",
];

const SetComponent = ({
  data,
  handleUpdate,
  handleDelete,
  handleAddDetail,
  translate,
  type,
  roles,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newSetName, setNewSetName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(data.title);
  const [setItem, setSetItem] = useState(data);
  const [setType] = useState(SET_TYPES[type] || null);
  const [isNewSubSetModalOpen, setIsNewSubSetModalOpen] = useState(false);
  const [informModalMessage, setInformModalMessage] = useState("");
  const [newSubSetItem, setNewSubSetItem] = useState({
    item: null,
    name: "",
    callback: () => {},
  });
  const [disableButtons, setDisableButtons] = useState(false);
  const [isSetUpdating, setIsSetUpdating] = useState(false);
  const { project } = useProject();

  const { setRequestLoading, requestLoading } = useContext(RequestContext);

  useEffect(() => {
    setSetItem(data);
  }, [data]);

  useEffect(() => {
    if (newSubSetItem.item && !isNewSubSetModalOpen) {
      setIsNewSubSetModalOpen(true);
    }

    return () => {
      if (isNewSubSetModalOpen) {
        setIsNewSubSetModalOpen(false);
        setNewSubSetItem({
          item: null,
          name: "",
          callback: () => {},
        });
      }
    };
  }, [isNewSubSetModalOpen, newSubSetItem.item]);

  const handleUpdateName = () => {
    if (newTitle.length > 0 && newTitle !== setItem.title) {
      API.putData(`${setType?.setUrl}/${setItem.id}`, { name: newTitle })
        .then(() => {
          setSetItem({ ...setItem, title: newTitle });
          setIsEditing(false);

          if (
            type === "LOCATION_SET" &&
            localStorage.getItem("selected-forms-location-root")
          ) {
            const locationRoot = JSON.parse(
              localStorage.getItem("selected-forms-location-root")
            );

            locationRoot.label = newTitle;

            localStorage.setItem(
              "selected-forms-location-root",
              JSON.stringify(locationRoot)
            );
          }
        })
        .catch(() => {
          setIsEditing(false);
        });
    } else setIsEditing(false);
  };

  const onDelete = (id) => {
    if (!id) return false;

    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                handleRemoveSubSet(id, onClose);
              },
              classNames: "btn btn-danger",
            },
          ]}
          message={translate("delete_confirmation")}
        />
      ),
    });
  };

  const handleRemoveSubSet = (id, closeModal) => {
    if (requestLoading) return;

    setRequestLoading(true);
    API.deleteData(`${setType?.subSetUrl}/${id}`).then(() => {
      const newData = setItem.subTree.filter((item) => item.id !== id);

      setSetItem({ ...setItem, subTree: newData });
      closeModal();
    });
  };

  const handleAddNewSubSet = (e) => {
    e.preventDefault();
    if (requestLoading) return;

    if (newSubSetItem?.name?.length > 0) {
      setDisableButtons(true);
      setRequestLoading(true);

      API.postData(
        `${setType?.setUrl}/${newSubSetItem?.item?.set_id}/details`,
        {
          name: newSubSetItem?.name,
          parent_id: newSubSetItem?.item.id,
        }
      )
        .then(({ data: res }) => {
          const { data } = res;
          newSubSetItem?.callback(data);

          setNewSubSetItem({ item: null });
          setDisableButtons(false);
        })
        .catch(() =>
          setTimeout(() => {
            setDisableButtons(false);
          }, 100)
        );
    }
  };

  const handleUpdateSingleItem = (newItem) => {
    const newData = setItem.subTree.map((item) => {
      if (item.id === newItem.id) {
        return newItem;
      }

      return item;
    });

    setSetItem({ ...setItem, subTree: newData });
  };

  const handleGetQR = (id) => {
    setDisableButtons(true);
    setInformModalMessage(
      translate("project_settings.location_sets.pdf_export_in_progress")
    );
    API.getData(`/site_set_qr/${id}`)
      .then(({ data: res }) => {
        const { data } = res;

        if (data) {
          window.open(data, "_self");
        }

        setDisableButtons(false);
      })
      .catch(() => {
        setDisableButtons(false);
      });
  };

  const handleUploadExcel = (e) => {
    e.preventDefault();

    const file = e.target.files[0];

    if (file) {
      setDisableButtons(true);
      setIsSetUpdating(true);
      setInformModalMessage(
        translate("project_settings.location_sets.excel_import_in_progress")
      );

      const formData = new FormData();
      formData.append("file", file);

      API.postFile(
        `${setType?.setUrl}/${setItem?.id}/import?project_id=${project?.id}`,
        formData,
      )
        .then(() => {
          API.getData(`site_set/${setItem.id}`)
            .then(({ data: res }) => {
              const data = res.data;

              const newSetItem = {
                ...setItem,
                subTree: data.details.map((detail) => ({
                  id: detail.id,
                  name: detail.name,
                  hasDetail: detail.is_details,
                  subTree: [],
                  set_id: setItem.id,
                })),
              };

              setSetItem(newSetItem);
              setDisableButtons(false);
              setIsSetUpdating(false);
            })
            .catch(() => {
              setDisableButtons(false);
              setIsSetUpdating(false);
            });
        })
        .catch(() => {
          setDisableButtons(false);
          setIsSetUpdating(false);
        });
    }
  };

  return (
    <div className="SetComponent w-100 d-flex flex-column">
      <div className="row">
        <div className="col-12">
          <h4 className="set-title d-flex justify-content-between align-items-center">
            <span className="text w-100 h-100 d-flex justify-content-between align-items-center">
              <input
                type="text"
                name="name"
                value={newTitle}
                readOnly={!isEditing}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  setIsEditing(true);
                }}
                className="w-100 me-4"
                onChange={(e) => setNewTitle(e.target.value)}
              />
              {isEditing && (
                <span
                  className="apply-button"
                  onClick={handleUpdateName}
                  role="button"
                >
                  <BiCheck size="20px" />
                </span>
              )}
            </span>
            <div className="right-side d-flex justify-content-end align-items-center">
              {setType?.qr && (
                <span
                  className="qr-button me-2"
                  onClick={() => handleGetQR(setItem.id)}
                  role="button"
                  data-is-disabled={disableButtons}
                >
                  <MdOutlineQrCode2 size="32px" />
                </span>
              )}
              <span
                className="add-new-button flex-shrink-0 rounded-circle bg-primary text-white"
                role="button"
                onClick={() => handleDelete(setItem.id)}
                data-is-disabled={disableButtons}
              >
                <BiMinus className="fs-3" />
              </span>
            </div>
          </h4>
        </div>
      </div>
      <div className="row" style={{ minHeight: "100%" }}>
        <div className="col-12">
          <div className="set-items">
            {isSetUpdating ? (
              <Loading />
            ) : (
              <div className="set-items-bg">
                {setItem.subTree.map((item) => (
                  <SubTreeItem
                    key={item.id}
                    item={item}
                    handleUpdateSingleItem={handleUpdateSingleItem}
                    handleRemoveSubSet={onDelete}
                    translate={translate}
                    setType={setType}
                    roles={roles}
                    setNewSubSetItem={(item, callback) =>
                      setNewSubSetItem({ item, callback, name: "" })
                    }
                    level={1}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 d-flex justify-content-center">
          <div
            className="set-footer my-2"
            onClick={() => {
              !isSetUpdating && setIsOpen(true);
            }}
            role="button"
            data-is-disabled={isSetUpdating}
          >
            <BiPlus className="fs-5 bg-primary text-white rounded-circle" />
          </div>
        </div>
      </div>
      {setType?.importFromExcel && (
        <div className="row">
          <div className="col-12">
            <label
              htmlFor={`import-button-${setItem.id}`}
              className="import-button text-center text-uppercase w-100"
              data-is-disabled={isSetUpdating}
            >
              <Translate
                id={`project_settings.${setType?.translateKey}.import_from_excel`}
              />
            </label>
            <input
              type="file"
              name="file"
              id={`import-button-${setItem.id}`}
              className="d-none"
              onChange={handleUploadExcel}
              disabled={isSetUpdating}
              accept=".xlsx, .xls, .csv, .xlsm"
            />
          </div>
        </div>
      )}
      {setType?.checkboxOptions && (
        <div className="row">
          <div className="col-12 d-flex justify-content-center pb-2 pt-1">
            <Checkbox
              checked={setItem[setType.checkboxOptions.name]}
              onChange={(value) =>
                handleUpdate(setItem.id, {
                  [setType.checkboxOptions.name]: value,
                })
              }
              id={uniqueId("multi-select-")}
              label={translate(
                `project_settings.${setType.translateKey}.${setType.checkboxOptions.label}`
              )}
            />
          </div>
        </div>
      )}

      <Modal
        title={translate(
          `project_settings.${setType?.translateKey}.create_new_sub_set`
        )}
        positiveButtonNode={translate("save")}
        negativeButtonNode={translate("cancel")}
        positiveButtonHandler={() =>
          handleAddDetail(setItem.id, { name: newSetName }, () => {
            setIsOpen(false);
            setNewSetName("");
          })
        }
        modalSize="lg"
        show={isOpen}
        setShow={(show) => {
          setIsOpen(show);
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAddDetail(setItem.id, { name: newSetName }, () => {
              setIsOpen(false);
              setNewSetName("");
            });
          }}
        >
          <div className="form-group">
            <Input
              type="text"
              name="newSetName"
              title={translate(
                `project_settings.${setType?.translateKey}.set_name`
              )}
              placeholder={translate(
                `project_settings.${setType?.translateKey}.set_name_placeholder`
              )}
              value={newSetName}
              onChange={(e) => setNewSetName(e.target.value)}
            />
          </div>
        </form>
      </Modal>

      <Modal
        title={translate(
          `project_settings.${setType?.translateKey}.create_new_sub_set`
        )}
        positiveButtonNode={translate("save")}
        negativeButtonNode={translate("cancel")}
        positiveButtonHandler={handleAddNewSubSet}
        modalSize="lg"
        show={isNewSubSetModalOpen}
        setShow={() => {
          setNewSubSetItem({
            item: null,
          });
        }}
        disableButtons={disableButtons}
      >
        <form onSubmit={handleAddNewSubSet}>
          <div className="form-group">
            <Input
              type="text"
              name="newSetName"
              title={translate("project_settings.location_sets.set_name")}
              placeholder={translate(
                "project_settings.location_sets.set_name_placeholder"
              )}
              value={newSubSetItem.name}
              onChange={(e) =>
                setNewSubSetItem({ ...newSubSetItem, name: e.target.value })
              }
            />
          </div>
        </form>
      </Modal>

      <InformModal
        showModal={disableButtons && !!informModalMessage}
        message={informModalMessage}
      />
    </div>
  );
};

export default withLocalize(SetComponent);
